import { ReactElement, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { Stack } from "@mui/material";
import { DrawerHeader, MobileCloseBtn } from "src/components/modal/basic-drawer/DrawerHeader";
import { DrawerStyle } from "src/components/modal/basic-drawer/BasicDrawer";
import Divider from "@mui/material/Divider";
import useResizeObserver from "@react-hook/resize-observer";

export interface DrawerContentProps {
    title?: string;
    style: DrawerStyle;
    TopComponent?: ReactElement;
    BottomComponent?: ReactElement;
    closeDrawerOnClick: () => void;
    isMobile: boolean;
    showDivider: boolean;
}

export const DrawerContent = ({
    title = "",
    style,
    TopComponent,
    BottomComponent,
    closeDrawerOnClick,
    isMobile,
    showDivider,
}: DrawerContentProps) => {
    return isMobile ? (
        <MobileSection
            TopComponent={TopComponent}
            BottomComponent={BottomComponent}
            closeDrawerOnClick={closeDrawerOnClick}
            style={style}
            title={title}
            showDivider={showDivider}
        />
    ) : (
        <DesktopSection
            TopComponent={TopComponent}
            BottomComponent={BottomComponent}
            closeDrawerOnClick={closeDrawerOnClick}
            style={style}
            title={title}
            showDivider={showDivider}
        />
    );
};

type ContentProps = Omit<DrawerContentProps, "isMobile">;

const DesktopSection = ({ title, style, TopComponent, BottomComponent, closeDrawerOnClick, showDivider }: ContentProps) => {
    const topSectionRef = useRef<HTMLElement>(null);
    const [desktopOffset, setDesktopOffset] = useState("0");

    useResizeObserver(topSectionRef, (entry) => {
        const newHeight = `${entry.target.clientHeight}px`;
        if (desktopOffset !== newHeight) {
            setDesktopOffset(newHeight);
        }
    });

    return (
        <Box
            sx={{
                overflow: "hidden",
                height: "100%",
                width: `calc(${style.modalWidth} - ${style.drawerPadding})`,
            }}
            data-testid={"desktop-content-section"}
        >
            {/* TOP SECTION - NOT SCROLLABLE */}
            <Box ref={topSectionRef} sx={{ mr: style.drawerPadding }}>
                <DrawerHeader closeDrawerOnClick={closeDrawerOnClick} title={title} mobile={false} />
                {TopComponent}
                {showDivider && <Divider sx={{ pt: "0.5rem" }} data-testid={"divider-id"} />}
            </Box>
            {/* BOTTOM SECTION - SCROLLABLE */}
            <Stack
                sx={{
                    overflow: "hidden",
                    mr: style.drawerPadding,
                    height: "100%",
                }}
                data-testid={"desktop-bottom-overflow-hidden"}
            >
                <Stack
                    sx={{
                        overflow: "auto",
                        height: `calc(100% - ${desktopOffset})`,
                    }}
                    pt={showDivider ? "1rem" : "0"}
                    pb="2rem"
                    data-testid={"desktop-bottom-overflow-auto"}
                >
                    {BottomComponent}
                </Stack>
            </Stack>
        </Box>
    );
};

const MobileSection = ({ title, style, TopComponent, BottomComponent, closeDrawerOnClick, showDivider }: ContentProps) => {
    return (
        <Stack
            data-testid={"mobile-content-section"}
            sx={{
                height: `calc(100% - ${style.mobileOffsetTop})`,
            }}
        >
            <MobileCloseBtn closeDrawerOnClick={closeDrawerOnClick} />
            {/* COMPLETE SECTION IS SCROLLABLE */}
            <Box
                sx={{
                    overflow: "auto",
                    height: `calc(100% - ${style.mobileOffsetTop})`,
                }}
            >
                <DrawerHeader closeDrawerOnClick={closeDrawerOnClick} title={title} mobile={true} />
                <Box pt={"1rem"} pb={showDivider ? "0" : "1.5rem"}>
                    {TopComponent}
                    {showDivider && <Divider sx={{ pt: "0.5rem" }} data-testid={"divider-id"} />}
                </Box>
                <Box pt={showDivider ? "1.5rem" : "0"} pb="1rem">
                    {BottomComponent}
                </Box>
            </Box>
        </Stack>
    );
};
